import { useParams, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { Users, Plus, Trash2, Loader2, Crown, Shield, Edit, ChevronRight } from 'lucide-react'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useBackButton } from '@/shared/hooks/useBackButton'
import { botsApi } from '@/shared/api/bots'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Toast from '@/components/ui/Toast'
import type { ListBotAccessResponse, BotAccessGrant, BotAccessRole } from '@/shared/types'

export default function BotAccessManagement() {
  const { botId } = useParams<{ botId: string }>()
  const navigate = useNavigate()
  const { hapticFeedback, hapticNotification, webApp } = useTelegram()
  const queryClient = useQueryClient()

  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [showToast, setShowToast] = useState(false)

  // Auto-manage back button
  useBackButton()

  // Fetch bot access data
  const { data: accessData, isLoading, error } = useQuery<ListBotAccessResponse>({
    queryKey: ['botAccess', botId],
    queryFn: () => botsApi.getBotAccess(botId!),
    enabled: !!botId,
    retry: 1,
  })

  // Revoke access mutation
  const revokeAccessMutation = useMutation({
    mutationFn: (telegramUserId: number) => botsApi.revokeBotAccess(botId!, telegramUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['botAccess', botId] })
      setToastMessage('Доступ успешно отозван')
      setToastType('success')
      setShowToast(true)
      hapticNotification('success')
    },
    onError: (error: any) => {
      const message = error.response?.data?.details || error.message || 'Не удалось отозвать доступ'
      setToastMessage(message)
      setToastType('error')
      setShowToast(true)
      hapticNotification('error')
    },
  })

  const handleRevokeAccess = (userId: number) => {
    webApp?.showPopup({
      title: 'Отозвать доступ',
      message: 'Вы уверены, что хотите отозвать доступ для этого пользователя?',
      buttons: [
        { id: 'cancel', type: 'cancel' },
        { id: 'confirm', type: 'destructive', text: 'Отозвать' },
      ],
    }, (buttonId) => {
      if (buttonId === 'confirm') {
        hapticFeedback('medium')
        revokeAccessMutation.mutate(userId)
      }
    })
  }

  const getRoleIcon = (role: BotAccessRole) => {
    if (role === 'owner') return <Crown className="w-4 h-4" />
    if (role === 'admin') return <Shield className="w-4 h-4" />
    return <Edit className="w-4 h-4" />
  }

  const getRoleColor = (role: BotAccessRole) => {
    if (role === 'owner') return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30'
    if (role === 'admin') return 'bg-purple-500/10 text-purple-600 border-purple-500/30'
    return 'bg-[#5288c1]/10 text-[#5288c1] border-[#5288c1]/30'
  }

  const getRoleLabel = (role: BotAccessRole) => {
    if (role === 'owner') return 'Владелец'
    if (role === 'admin') return 'Админ'
    return 'Редактор'
  }

  const getUserDisplayName = (grant: BotAccessGrant) => {
    if (grant.first_name || grant.last_name) {
      return `${grant.first_name || ''} ${grant.last_name || ''}`.trim()
    }
    if (grant.username) {
      return `@${grant.username}`
    }
    return `Пользователь ${grant.telegram_user_id}`
  }


  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-tg-bg">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="text-red-500 mb-2 font-semibold">Ошибка загрузки данных доступа</div>
          <div className="text-sm text-tg-hint mb-4">{(error as any)?.message || 'Неизвестная ошибка'}</div>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-tg-button text-tg-button-text rounded-xl hover:opacity-90 active:opacity-75 transition-opacity"
          >
            Назад
          </button>
        </div>
      </div>
    )
  }

  if (!accessData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-tg-bg">
        <div className="text-tg-hint">Нет доступных данных</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#5288c1]/20 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-[#5288c1]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-tg-text">Управление доступом</h1>
              <p className="text-sm text-tg-hint mt-0.5">Управляйте правами доступа</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* Owner Section */}
        <div className="bg-tg-secondary-bg rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                <Crown className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <h2 className="font-semibold text-tg-text">Владелец бота</h2>
                <p className="text-xs text-tg-hint">Создатель и владелец</p>
              </div>
            </div>
          </div>

          <div className="p-4">
            {accessData.owner && (
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-tg-text truncate">
                    {getUserDisplayName(accessData.owner)}
                  </div>
                  <div className="text-sm text-tg-hint">
                    ID: {accessData.owner.telegram_user_id}
                  </div>
                </div>
                <div className={`px-3 py-1.5 rounded-full text-xs font-medium border ${getRoleColor('owner')}`}>
                  <div className="flex items-center gap-1.5">
                    {getRoleIcon('owner')}
                    {getRoleLabel('owner')}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add User Button */}
        <button
          onClick={() => {
            hapticFeedback('light')
            window.scrollTo(0, 0)
            navigate(`/bots/${botId}/access/add`)
          }}
          className="w-full bg-tg-button text-tg-button-text rounded-xl p-4 hover:opacity-90 active:opacity-75 transition-opacity flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Plus className="w-5 h-5" />
            </div>
            <div className="text-left">
              <div className="font-semibold">Добавить пользователя</div>
              <div className="text-sm opacity-90">Предоставить доступ другому пользователю</div>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 opacity-75" />
        </button>

        {/* Granted Access Section */}
        <div className="bg-tg-secondary-bg rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#5288c1]/20 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-[#5288c1]" />
              </div>
              <div>
                <h2 className="font-semibold text-tg-text">Предоставленный доступ ({accessData.total_count || 0})</h2>
                <p className="text-xs text-tg-hint">Пользователи с правами редактирования</p>
              </div>
            </div>
          </div>

          {!accessData.grants || accessData.grants.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-tg-hint font-medium">Пока нет пользователей с доступом</p>
            </div>
          ) : (
            <div className="divide-y divide-tg-secondary-bg">
              {accessData.grants.map((grant) => (
                <div key={grant.telegram_user_id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0 mr-3">
                      <div className="font-medium text-tg-text truncate">
                        {getUserDisplayName(grant)}
                      </div>
                      <div className="text-sm text-tg-hint">
                        ID: {grant.telegram_user_id}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className={`px-3 py-1.5 rounded-full text-xs font-medium border ${getRoleColor(grant.role)}`}>
                        <div className="flex items-center gap-1.5">
                          {getRoleIcon(grant.role)}
                          {getRoleLabel(grant.role)}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRevokeAccess(grant.telegram_user_id)}
                        disabled={revokeAccessMutation.isPending}
                        className="p-2 text-red-600 hover:bg-red-500/10 rounded-lg transition-colors active:scale-95 disabled:opacity-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  )
}
